import { Card, CardContent } from "@/components/ui/card";
import type { FeedbackAnalytics, FeedbackBreakdownItem } from "@/lib/api";

import { AnswerFeedbackSection } from "./answer-feedback-section";
import { FeedbackExplorer } from "./feedback-explorer";

interface Props {
  analytics: FeedbackAnalytics;
}

/** A part's reliability bucket, derived from its correct/incorrect counts. */
type Reliability = "always-right" | "mixed" | "always-wrong";

function reliabilityOf(item: FeedbackBreakdownItem): Reliability {
  if (item.incorrect === 0) {
    return "always-right";
  }
  if (item.correct === 0) {
    return "always-wrong";
  }
  return "mixed";
}

const GROUPS: {
  key: Reliability;
  title: string;
  hint: string;
  badgeClass: string;
}[] = [
  {
    key: "always-right",
    title: "Always correct",
    hint: "Every rating so far agrees on these.",
    badgeClass: "bg-green-100 text-green-800",
  },
  {
    key: "mixed",
    title: "Mixed",
    hint: "Right sometimes, wrong other times — worth a closer look.",
    badgeClass: "bg-amber-100 text-amber-800",
  },
  {
    key: "always-wrong",
    title: "Always wrong",
    hint: "Never rated correct — likely a real matching bug.",
    badgeClass: "bg-red-100 text-red-700",
  },
];

export function FeedbackDashboard({ analytics }: Props) {
  const { summary, breakdown, answers } = analytics;

  const stats = [
    { label: "Total ratings", value: String(summary.total) },
    { label: "Correct", value: String(summary.correct) },
    { label: "Incorrect", value: String(summary.incorrect) },
    { label: "Accuracy", value: `${Math.round(summary.accuracy_pct)}%` },
  ];

  const grouped = GROUPS.map((group) => ({
    ...group,
    items: breakdown.filter((item) => reliabilityOf(item) === group.key),
  }));

  return (
    <div className="space-y-8">
      <section>
        <h2 className="font-heading text-primary text-lg font-bold">Part accuracy</h2>
        <p className="text-muted mb-3 text-xs">
          Correct or incorrect on each suggested part — whether the tool picked the right part.
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="py-4">
                <p className="text-muted text-xs">{stat.label}</p>
                <p className="text-primary text-2xl font-bold">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {breakdown.length > 0 && (
        <section>
          <h2 className="font-heading text-primary mb-3 text-sm font-bold">Overview by request</h2>
          <p className="text-muted mb-3 text-xs">
            Every distinct request wording (as technicians phrased it), grouped by whether the tool
            consistently gets it right.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            {grouped.map((group) => (
              <Card key={group.key}>
                <CardContent className="space-y-3 py-4">
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${group.badgeClass}`}
                    >
                      {group.title}
                    </span>
                    <span className="text-primary text-lg font-bold">{group.items.length}</span>
                  </div>
                  <p className="text-muted text-xs">{group.hint}</p>
                  {group.items.length > 0 && (
                    <ul className="space-y-1.5">
                      {group.items.map((item) => (
                        <li key={item.requested_item} className="truncate text-sm">
                          <span className="font-medium">{item.requested_item}</span>
                          {group.key === "mixed" && (
                            <span className="text-muted ml-1 text-xs">
                              ({item.correct}/{item.total} correct)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {breakdown.length > 0 && (
        <section>
          <h2 className="font-heading text-primary mb-3 text-sm font-bold">Detail — by request</h2>
          <p className="text-muted mb-3 text-xs">
            Same requests as above, as a sortable-by-eye list ordered by how often the tool gets
            them wrong.
          </p>
          <Card>
            <CardContent className="space-y-2 py-4">
              {breakdown.map((item) => (
                <div
                  key={item.requested_item}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <div className="min-w-0 truncate font-medium">{item.requested_item}</div>
                  <div className="flex shrink-0 items-center gap-4 text-right">
                    <span className="text-muted text-xs">
                      {item.incorrect} of {item.total} wrong
                    </span>
                    <span className="text-primary w-12 font-bold">
                      {Math.round(item.accuracy_pct)}%
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <section>
        <h2 className="font-heading text-primary mb-3 text-sm font-bold">Detail — every rating</h2>
        <FeedbackExplorer />
      </section>

      <AnswerFeedbackSection summary={answers} />
    </div>
  );
}
